/**
 * Hermes stores executor/gateway nudges as role=user; the UI shows them as EAD Assistant.
 */

const RUNNING_BOOTSTRAP_MARKER = "[ead-running-bootstrap:v1]";
const RUNNING_PFM_GUIDANCE_MARKER = "[ead-running-pfm-guidance:v1]";
const PHASE1_LOGIN_ATTEMPT_MARKER = "[ead-login-phase-attempt:v1]";
const PHASE1_LOGIN_REMINDER_MARKER = "[ead-login-activity-log-reminder:v1]";
const PHASE_PROMOTED_RUNNING_MARKER = "[ead-phase-promoted:running:v1]";

const EAD_ASSISTANT_PREFIXES = [
  "continue from the last successful browser/tool state.",
  "run initialized. await executor phase assignment.",
  "login phase is required first.",
  "login confirmed. start pfm discovery phase now.",
  "refresh requested by operator:",
  "publish a new operator activity log now",
] as const;

const EAD_ASSISTANT_MARKERS = [
  "[executor activity reminder",
  "[executor cadence",
  "[executor running-phase reminder",
  "[executor pfm delivery reminder",
  "[executor pfm post-delivery",
  "[executor pfm flat-delivery treelist fix:v1]",
  "[executor pfm consolidation delivery",
  "[executor pfm delivery rejected",
  "[ead-login-success-check]",
  "[ead-login-phase-attempt:v1]",
  "[ead-login-activity-log-reminder:v1]",
  "[ead-running-pfm-guidance:v1]",
  "[ead-running-bootstrap:v1]",
  "[ead-running-phase-ii-prompt:v1]",
  "[ead-phase-promoted:running:v1]",
  "[ead-login-checkpoint:v1]",
  "phase i assignment:",
  "phase ii assignment:",
  "two-minute operator cadence",
  "operator activity cadence",
  "formal login checkpoint required",
  "login confirmed. initialization complete. **running phase** starts now.",
  "login confirmed. **running phase** starts now.",
] as const;

export function isRunningCadenceReminderMessage(content: string): boolean {
  const lower = String(content || "").trim().toLowerCase();
  if (!lower) return false;
  return (
    lower.includes("[executor pfm delivery reminder") ||
    lower.includes("[executor running-phase reminder") ||
    lower.includes("[executor pfm consolidation delivery") ||
    lower.includes("[executor reminder] activity cadence") ||
    lower.includes("[executor] pfm delivery accepted")
  );
}

export function isRunningBootstrapInjectMessage(content: string): boolean {
  return String(content || "").trim().toLowerCase().includes(RUNNING_BOOTSTRAP_MARKER);
}

export function isPhase1LoginInjectMessage(content: string): boolean {
  const lower = String(content || "").trim().toLowerCase();
  return (
    lower.includes(PHASE1_LOGIN_ATTEMPT_MARKER) ||
    lower.includes(PHASE1_LOGIN_REMINDER_MARKER) ||
    lower.includes("[ead-login-success-check]")
  );
}

export function isRunningPfmGuidanceMessage(content: string): boolean {
  return String(content || "").trim().toLowerCase().includes(RUNNING_PFM_GUIDANCE_MARKER);
}

export function isPhasePromotedRunningMessage(content: string): boolean {
  return String(content || "").trim().toLowerCase().includes(PHASE_PROMOTED_RUNNING_MARKER);
}

const PHASE_SYNC_MARKERS = [
  "[ead-phase-promoted:running:v1]",
  "promoted this run to phase ii running",
  "[ead-running-bootstrap:v1]",
  "[ead-running-phase-ii-prompt:v1]",
  "running phase** starts now",
  "login confirmed. initialization complete.",
] as const;

export function shouldSyncExecutionPhaseFromChat(content: string): boolean {
  const lower = String(content || "").trim().toLowerCase();
  if (!lower) return false;
  return PHASE_SYNC_MARKERS.some((marker) => lower.includes(marker));
}

export function isEadAssistantInjectMessage(content: string): boolean {
  const text = String(content || "").trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  for (const prefix of EAD_ASSISTANT_PREFIXES) {
    if (lower.startsWith(prefix)) return true;
  }
  for (const marker of EAD_ASSISTANT_MARKERS) {
    if (lower.includes(marker)) return true;
  }
  return false;
}
